import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Moon, Sparkles, Heart } from 'lucide-react';
import { LunarPhaseData } from '@/utils/lunarPhases';

interface LunarWisdomTeachingsProps {
  lunarPhase: LunarPhaseData;
}

export default function LunarWisdomTeachings({ lunarPhase }: LunarWisdomTeachingsProps) {
  
  const getWitchWisdom = () => {
    switch (lunarPhase.phase) {
      case 'new_moon':
        return {
          witch: 'Dark Moon / New Beginnings',
          description: 'The New Moon is a time of rest and introspection. In traditional moon wisdom, this is when the Red Witch (menstruating woman) holds the most power - a time for deep rest, visions, and inner wisdom.',
          ceremony: 'Set intentions, plant seeds for new beginnings, rest deeply',
          energy: 'Introspective, visionary, receptive',
          color: 'Deep indigo and black'
        };
      
      case 'waxing_crescent':
      case 'first_quarter':
      case 'waxing_gibbous':
        return {
          witch: 'White Witch / Maiden Energy',
          description: 'The Waxing Moon is the time of the White Witch - the maiden energy that is dynamic, outward-focused, and building. This corresponds to the follicular phase when estrogen rises and energy builds.',
          ceremony: 'Take action on intentions, build projects, socialise, create',
          energy: 'Dynamic, outward, building, creative',
          color: 'White, silver, and light blue'
        };
      
      case 'full_moon':
        return {
          witch: 'Peak Power / Mother Energy',
          description: 'The Full Moon is the peak of lunar power - a time of completion, celebration, and releasing. In menstrual wisdom, this is when the White Witch (at ovulation) holds peak outward power and fertility.',
          ceremony: 'Release what no longer serves, charge crystals and water, celebrate achievements, perform powerful manifestation work',
          energy: 'Peak power, completion, release, gratitude',
          color: 'Bright silver and gold'
        };
      
      case 'waning_gibbous':
      case 'last_quarter':
      case 'waning_crescent':
        return {
          witch: 'Red Witch Returning / Crone Wisdom',
          description: 'The Waning Moon brings crone wisdom - time to winnow, discern, and prepare for the inward journey. This corresponds to the luteal phase when we turn inward and prepare for menstruation.',
          ceremony: 'Banish negativity, clear space, practice gratitude, prepare for rest',
          energy: 'Inward, reflective, clearing, wise',
          color: 'Deep red, burgundy, and purple'
        };
      
      default:
        return {
          witch: 'Lunar Wisdom',
          description: 'Honor the moon\'s guidance',
          ceremony: 'Attune to lunar rhythms',
          energy: 'Receptive',
          color: 'Silver'
        };
    }
  };

  const wisdom = getWitchWisdom();

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Moon className="h-5 w-5" />
          {lunarPhase.name} Wisdom
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Witch Type */}
        <div className="p-4 rounded-lg bg-background/50 border-2 border-primary/30">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-lg">{wisdom.witch}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{wisdom.description}</p>
        </div>

        {/* Ceremony Suggestions */}
        <div className="p-3 rounded-lg bg-background/30">
          <h4 className="font-medium flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Ceremony & Practice
          </h4>
          <p className="text-sm">{wisdom.ceremony}</p>
        </div>

        {/* Energy Type */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-background/30">
            <h4 className="font-medium text-xs mb-1">Energy</h4>
            <p className="text-sm">{wisdom.energy}</p>
          </div>
          <div className="p-3 rounded-lg bg-background/30">
            <h4 className="font-medium text-xs mb-1">Sacred Colours</h4>
            <p className="text-sm">{wisdom.color}</p>
          </div>
        </div>

        {/* Red Witch vs White Witch Teaching */}
        <div className="p-4 rounded-lg border-2 border-pink-300 dark:border-pink-700 bg-gradient-to-r from-red-50 to-white dark:from-red-950/20 dark:to-slate-900">
          <h4 className="font-semibold flex items-center gap-2 mb-2">
            <Heart className="h-4 w-4" />
            Ancient Moon Wisdom
          </h4>
          <div className="space-y-2 text-sm">
            <p>
              <strong className="text-red-600 dark:text-red-400">Red Witch</strong> (Menstruating/Dark Moon): Time of deep rest, inner vision, and psychic power. The veil is thin. You hold prophetic wisdom.
            </p>
            <p>
              <strong>White Witch</strong> (Ovulating/Full Moon): Time of peak outward energy, fertility, creation, and dynamic action. You hold manifesting power.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Traditional cultures honoured menstruating women as priestesses and visionaries, recognising the power of cyclical wisdom.
            </p>
          </div>
        </div>

        {/* Call to Action Based on Phase */}
        <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
          <p className="text-sm font-medium">
            {lunarPhase.phase === 'new_moon' && 'New Moon: Time to rest, vision, and call in new beginnings'}
            {lunarPhase.phase === 'full_moon' && 'Full Moon: Time to celebrate, release, and give gratitude'}
            {lunarPhase.isWaxing && lunarPhase.phase !== 'new_moon' && 'Waxing Phase: Time to build, create, and take action'}
            {!lunarPhase.isWaxing && lunarPhase.phase !== 'full_moon' && 'Waning Phase: Time to clear, reflect, and let go'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
